import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Link,
  Chip,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Grid,
  Alert,
  Fab,
  InputAdornment,
  TablePagination,
  Autocomplete,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { 
  Add as AddIcon, 
  AttachFile as AttachFileIcon,
  AutoAwesome as AutoAwesomeIcon,
  Delete as DeleteIcon, 
  Search as SearchIcon, 
  Clear as ClearIcon,
  Visibility as VisibilityIcon 
} from '@mui/icons-material';
import { format } from 'date-fns';
import { projectsApi, purchaseOrdersApi, tenantApi } from '../services/api';
import SavedItemsSearch from '../components/SavedItemsSearch';
import VendorSelector from '../components/VendorSelector';
import { AppContext } from '../App';
import { compressImages } from '../utils/imageCompression';
import { limitFilesForUpload } from '../utils/uploadLimits';

function PurchaseOrders() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { currentUser } = useContext(AppContext);
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };
  
  // Calculate total for a purchase order
  const calculateTotal = (items) => {
    if (!items || !items.length) return 0;
    return items.reduce((sum, item) => {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.price) || 0;
      return sum + (qty * price);
    }, 0);
  };

  // Calculate NETT total (before VAT)
  const calculateNettTotal = (items) => {
  return calculateTotal(items || []);
  };

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [filteredPurchaseOrders, setFilteredPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [purchaseOrderToDelete, setPurchaseOrderToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [tenantBranding, setTenantBranding] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Duplication mode state
  const [isDuplicationMode, setIsDuplicationMode] = useState(false);
  const [selectedPOToDuplicate, setSelectedPOToDuplicate] = useState(null);
  
  // Form state for creating a standalone or project-linked purchase order
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [purchaseOrderDepartment, setPurchaseOrderDepartment] = useState(currentUser?.department || 'General');
  
  const [deliveryAddress, setDeliveryAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    postcode: ''
  });
  const [reference, setReference] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [items, setItems] = useState([
    { name: '', quantity: 1, units: 'Each', price: 0 }
  ]);
  const [formErrors, setFormErrors] = useState({});
  
  // Saved items search states
  const [savedItemsSearches, setSavedItemsSearches] = useState(['']);
  const extractionFileInputRef = useRef(null);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [extractingItems, setExtractingItems] = useState(false);
  const [documentExtractionError, setDocumentExtractionError] = useState('');
  const [documentExtractionWarnings, setDocumentExtractionWarnings] = useState([]);
  const [documentExtractionNotes, setDocumentExtractionNotes] = useState('');

  // Fetch departments from tenant branding
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await tenantApi.getBranding();
        const depts = response.data?.branding?.departments;
        if (Array.isArray(depts) && depts.length > 0) {
          setDepartments(depts);
          if (currentUser?.department && depts.includes(currentUser.department)) {
            setDepartmentFilter(currentUser.department);
          }
        }
      } catch (err) {
        console.error('Error fetching departments:', err);
      }
    };
    fetchDepartments();
  }, [currentUser?.department]);

  // Filter purchase orders based on search query and department
  useEffect(() => {
    let filtered = purchaseOrders;

    if (departmentFilter !== 'all') {
      filtered = filtered.filter(po => po.department === departmentFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      const poNumberQuery = query.replace(/^po(?=[\s#-]|\d)\s*[-#]?\s*/, '');
      const projectNumberQuery = query.replace(/^project(?=[\s#-]|\d)\s*[-#]?\s*/, '');
      filtered = filtered.filter(po => 
        po.vendor_name?.toLowerCase().includes(query) ||
        (po.purchase_order_no && po.purchase_order_no.toString().toLowerCase().includes(poNumberQuery)) ||
        (po.purchase_order_id && po.purchase_order_id.toString().toLowerCase().includes(poNumberQuery)) ||
        (po.project_no && po.project_no.toString().toLowerCase().includes(projectNumberQuery)) ||
        (po.project_id && po.project_id.toString().toLowerCase().includes(projectNumberQuery)) ||
        (po.project_organisation_name && po.project_organisation_name.toLowerCase().includes(query)) ||
        (po.project_address_line1 && po.project_address_line1.toLowerCase().includes(query)) ||
        (po.project_city && po.project_city.toLowerCase().includes(query)) ||
        (po.project_postcode && po.project_postcode.toLowerCase().includes(query)) ||
        (po.reference && po.reference.toLowerCase().includes(query)) ||
        (po.created_by_name && po.created_by_name.toLowerCase().includes(query))
      );
    }

    setFilteredPurchaseOrders(filtered);
    // Reset to first page when filters change
    setPage(0);
  }, [searchQuery, departmentFilter, purchaseOrders]);

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get paginated data
  const paginatedPurchaseOrders = filteredPurchaseOrders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const hasDepartments = departments.length > 0;
  const departmentOptions = Array.from(
    new Set([...(departments || []), currentUser?.department, selectedProject?.department].filter(Boolean))
  );
  if (departmentOptions.length === 0) {
    departmentOptions.push('General');
  }
  const getDefaultPurchaseOrderDepartment = () => currentUser?.department || departmentOptions[0] || 'General';
  const getProjectLabel = (project) => {
    const projectNumber = project.project_no || project.project_id;
    const name = project.organisation_name || project.quote_reference;
    return `Project #${projectNumber}${name ? ` - ${name}` : ''}`;
  };

  const handleProjectChange = (project) => {
    setSelectedProject(project);
    setPurchaseOrderDepartment(project?.department || getDefaultPurchaseOrderDepartment());
    if (formErrors.project) {
      setFormErrors(prev => ({ ...prev, project: '' }));
    }
  };

  // Handle department filter change
  const handleDepartmentFilterChange = (event) => {
    setDepartmentFilter(event.target.value);
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Navigate to purchase order detail
  const handleViewPurchaseOrder = (poId) => {
    navigate(`/purchase-orders/${poId}`);
  };

  const handleOpenDeleteDialog = (po) => {
    setPurchaseOrderToDelete(po);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setPurchaseOrderToDelete(null);
  };

  const handleDeletePurchaseOrder = async () => {
    if (!purchaseOrderToDelete) return;

    try {
      setDeleting(true);
      await purchaseOrdersApi.delete(purchaseOrderToDelete.purchase_order_id);
      handleCloseDeleteDialog();
      await fetchPurchaseOrders();
    } catch (err) {
      console.error('Error deleting purchase order:', err);
      setError('Failed to delete purchase order. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Mobile purchase order card component
  const PurchaseOrderCard = ({ po }) => (
    <Card sx={{ mb: 2, cursor: 'pointer' }} onClick={() => handleViewPurchaseOrder(po.purchase_order_id)}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" component="div" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            PO #{po.purchase_order_no || po.purchase_order_id}
          </Typography>
          <Chip 
            label={formatCurrency(po.items ? calculateNettTotal(po.items) : 0)}
            color="primary"
            size="small"
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <strong>Vendor:</strong> {po.vendor_name || '-'}
        </Typography>
        <Box sx={{ mb: 1 }}>
          <Chip 
            label={po.status || 'New'} 
            color={po.status === 'Completed' ? 'success' : 'default'}
            size="small"
          />
        </Box>
        {po.reference && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Reference:</strong> {po.reference}
          </Typography>
        )}
        {po.due_date && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Due Date:</strong> {format(new Date(po.due_date), 'dd MMM yyyy')}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <strong>Items:</strong> {po.items ? po.items.length : 0} items
        </Typography>
        {po.project_id ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Project:</strong> #{po.project_no || po.project_id} - {po.project_address_line1}
            {po.project_address_line2 ? `, ${po.project_address_line2}` : ''}
            {po.project_city ? `, ${po.project_city}` : ''}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Type:</strong> Standalone Purchase Order
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" display="block">
          Created: {po.created_date ? format(new Date(po.created_date), 'dd MMM yyyy') : 'N/A'}
          {po.created_by_name && ` by ${po.created_by_name}`}
        </Typography>
      </CardContent>
      <CardActions sx={{ pt: 0, pb: 2, px: 2 }}>
        <Button 
          size="small" 
          startIcon={<VisibilityIcon />}
          onClick={(e) => {
            e.stopPropagation();
            handleViewPurchaseOrder(po.purchase_order_id);
          }}
          fullWidth
        >
          View Details
        </Button>
        <Button
          size="small"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenDeleteDialog(po);
          }}
          fullWidth
          aria-label={`Delete purchase order ${po.purchase_order_id}`}
        >
          Delete
        </Button>
      </CardActions>
    </Card>
  );

  // Helper to prefill delivery address
  const prefillAddress = (type) => {
    if (type === 'project' && selectedProject) {
      setDeliveryAddress({
        line1: selectedProject.address_line1 || '',
        line2: selectedProject.address_line2 || '',
        city: selectedProject.city || '',
        postcode: selectedProject.postcode || ''
      });
      return;
    }

    if (type === 'company' && tenantBranding?.company_address) {
      // Parse company address string properly
      const companyAddress = tenantBranding.company_address;
      const parts = companyAddress.split(',').map(s => s.trim());
      
      // UK postcode pattern - match at the end of the last part
      const lastPart = parts[parts.length - 1] || '';
      const postcodeMatch = lastPart.match(/([A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2})$/i);
      
      let postcode = '';
      let addressParts = [...parts];
      
      // If we found a postcode in the last part, extract it
      if (postcodeMatch) {
        postcode = postcodeMatch[1].toUpperCase();
        // Remove the postcode from the last part to get the city
        const cityFromLastPart = lastPart.substring(0, lastPart.length - postcode.length).trim();
        
        // Replace the last part with just the city (postcode removed)
        if (cityFromLastPart) {
          addressParts[parts.length - 1] = cityFromLastPart;
        } else {
          // If last part was only the postcode, remove it
          addressParts.pop();
        }
      }
      
      // Now split the remaining parts into line1, line2, and city
      const line1 = addressParts[0] || '';
      const line2 = addressParts[1] || '';
      const city = addressParts.length > 2 ? addressParts.slice(2).join(', ') : '';
      
      setDeliveryAddress({
        line1: line1,
        line2: line2,
        city: city,
        postcode: postcode
      });
    }
  };

  // Handle form input changes
  const handleVendorChange = (vendor) => {
    setSelectedVendor(vendor);
    if (formErrors.vendor) {
      setFormErrors(prev => ({ ...prev, vendor: '' }));
    }
  };

  const handleDeliveryAddressChange = (field, value) => {
    setDeliveryAddress(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
    
    // Clear any item-specific errors
    if (formErrors[`item_${index}_${field}`]) {
      setFormErrors(prev => ({ ...prev, [`item_${index}_${field}`]: '' }));
    }
  };

  const addItem = () => {
    setItems([...items, { name: '', quantity: 1, units: 'Each', price: 0 }]);
    setSavedItemsSearches([...savedItemsSearches, '']);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
      const newSearches = savedItemsSearches.filter((_, i) => i !== index);
      setSavedItemsSearches(newSearches);
    }
  };

  // Handle saved item selection
  const handleSavedItemSelect = (index, savedItem) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      name: savedItem.name,
      units: savedItem.units || 'Each'
    };
    setItems(newItems);
    
    // Clear the search for this item
    const newSearches = [...savedItemsSearches];
    newSearches[index] = '';
    setSavedItemsSearches(newSearches);
  };

  // Handle saved items search change
  const handleSavedItemsSearchChange = (index, value) => {
    const newSearches = [...savedItemsSearches];
    newSearches[index] = value;
    setSavedItemsSearches(newSearches);
  };

  const isSupportedExtractionFile = (file) => {
    const fileType = (file?.type || '').toLowerCase();
    const fileName = (file?.name || '').toLowerCase();

    return (
      fileType.startsWith('image/') ||
      fileType === 'application/pdf' ||
      fileType === 'message/rfc822' ||
      fileName.endsWith('.pdf') ||
      fileName.endsWith('.eml') ||
      fileName.endsWith('.txt') ||
      fileName.endsWith('.html') ||
      fileName.endsWith('.htm')
    );
  };

  const hasMeaningfulItems = (currentItems) => {
    return currentItems.some((item) => {
      const name = item.name?.trim();
      const quantity = Number(item.quantity);
      const price = Number(item.price);
      const units = (item.units || 'Each').trim();

      return Boolean(
        name ||
        quantity !== 1 ||
        price !== 0 ||
        units !== 'Each'
      );
    });
  };

  const clearDocumentExtractionState = () => {
    setDocumentFiles([]);
    setDocumentExtractionError('');
    setDocumentExtractionWarnings([]);
    setDocumentExtractionNotes('');
    if (extractionFileInputRef.current) {
      extractionFileInputRef.current.value = '';
    }
  };

  const handleDocumentFileSelect = async (event) => {
    const { files: selectedFiles, warning } = limitFilesForUpload(event.target.files || []);
    if (warning) {
      setDocumentExtractionError(warning);
      return;
    }

    const validFiles = selectedFiles.filter(isSupportedExtractionFile);

    if (validFiles.length !== selectedFiles.length) {
      setDocumentExtractionError('Some files were skipped. Only PDFs, images, and email files are supported.');
    } else {
      setDocumentExtractionError('');
    }

    if (!validFiles.length) {
      if (extractionFileInputRef.current) {
        extractionFileInputRef.current.value = '';
      }
      return;
    }

    try {
      const processedFiles = await compressImages(validFiles);
      setDocumentFiles((prev) => [...prev, ...processedFiles]);
      setDocumentExtractionWarnings([]);
      setDocumentExtractionNotes('');
    } catch (error) {
      console.error('Error preparing document files:', error);
      setDocumentExtractionError('Failed to prepare the selected file. Please try another document.');
    } finally {
      if (extractionFileInputRef.current) {
        extractionFileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveDocumentFile = (index) => {
    setDocumentFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
  };

  const handleExtractItemsFromDocument = async () => {
    if (!documentFiles.length) {
      setDocumentExtractionError('Upload a PDF, image, or email file first.');
      return;
    }

    setExtractingItems(true);
    setDocumentExtractionError('');

    try {
      const formData = new FormData();
      documentFiles.forEach((file) => {
        formData.append('files', file);
      });

      const response = await purchaseOrdersApi.extractItems(formData);
      const extractedItems = Array.isArray(response.data?.items) ? response.data.items : [];

      if (!extractedItems.length) {
        setDocumentExtractionError('No items were found in the uploaded document.');
        return;
      }

      const normalizedItems = extractedItems.map((item) => ({
        name: item.name || '',
        quantity: item.quantity || 1,
        units: item.units || 'Each',
        price: item.price || 0,
      }));

      const shouldReplaceItems = !hasMeaningfulItems(items);
      const nextItems = shouldReplaceItems ? normalizedItems : [...items, ...normalizedItems];
      const nextSearches = shouldReplaceItems
        ? new Array(normalizedItems.length).fill('')
        : [...savedItemsSearches, ...new Array(normalizedItems.length).fill('')];

      setItems(nextItems);
      setSavedItemsSearches(nextSearches);
      setDocumentExtractionWarnings(Array.isArray(response.data?.warnings) ? response.data.warnings : []);
      setDocumentExtractionNotes(response.data?.notes || '');
      setFormErrors((prev) => {
        const nextErrors = { ...prev };
        Object.keys(nextErrors).forEach((key) => {
          if (key.startsWith('item_')) {
            delete nextErrors[key];
          }
        });
        return nextErrors;
      });
      setDocumentFiles([]);
    } catch (error) {
      console.error('Error extracting purchase order items:', error);
      setDocumentExtractionError(
        error.response?.data?.error || 'Failed to extract items from the uploaded document.'
      );
    } finally {
      setExtractingItems(false);
      if (extractionFileInputRef.current) {
        extractionFileInputRef.current.value = '';
      }
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!selectedVendor) {
      errors.vendor = 'Vendor is required';
    }
    
    if (!reference.trim()) {
      errors.reference = 'Reference is required';
    }
    
    items.forEach((item, index) => {
      if (!item.name.trim()) {
        errors[`item_${index}_name`] = 'Item name is required';
      }
      if (!item.quantity || item.quantity <= 0) {
        errors[`item_${index}_quantity`] = 'Quantity must be greater than 0';
      }
      if (!item.price || item.price <= 0) {
        errors[`item_${index}_price`] = 'Price must be greater than 0';
      }
    });
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreatePO = async () => {
    if (!validateForm()) {
      return;
    }
    
    setCreating(true);
    try {
      const data = {
        project_id: selectedProject?.project_id || null,
        vendor_id: selectedVendor.vendor_id,
        department: purchaseOrderDepartment || getDefaultPurchaseOrderDepartment(),
        items: items.map(item => ({
          name: item.name.trim(),
          quantity: parseInt(item.quantity),
          units: item.units || 'Each',
          price: parseFloat(item.price)
        })),
        delivery_address_line1: deliveryAddress.line1.trim() || null,
        delivery_address_line2: deliveryAddress.line2.trim() || null,
        delivery_city: deliveryAddress.city.trim() || null,
        delivery_postcode: deliveryAddress.postcode.trim() || null,
        reference: reference.trim() || null,
        due_date: dueDate || null,
        contact_name: contactName.trim() || null,
        contact_number: contactNumber.trim() || null
      };
      
      await purchaseOrdersApi.createStandalone(data);
      
      // Reset form and close dialog
      setSelectedVendor(null);
      setSelectedProject(null);
      setDeliveryAddress({ line1: '', line2: '', city: '', postcode: '' });
      setItems([{ name: '', quantity: 1, units: 'Each', price: 0 }]);
      setReference('');
      setDueDate('');
      setContactName('');
      setContactNumber('');
      setPurchaseOrderDepartment(getDefaultPurchaseOrderDepartment());
      setFormErrors({});
      clearDocumentExtractionState();
      setCreateDialogOpen(false);
      
      // Refresh the purchase orders list
      fetchPurchaseOrders();
    } catch (error) {
      console.error('Error creating purchase order:', error);
      setFormErrors({ general: 'Failed to create purchase order. Please try again.' });
    } finally {
      setCreating(false);
    }
  };

  const handleCloseDialog = () => {
    setCreateDialogOpen(false);
    setIsDuplicationMode(false);
    setSelectedPOToDuplicate(null);
    setSelectedVendor(null);
    setSelectedProject(null);
    setPurchaseOrderDepartment(getDefaultPurchaseOrderDepartment());
    setDeliveryAddress({ line1: '', line2: '', city: '', postcode: '' });
    setItems([{ name: '', quantity: 1, units: 'Each', price: 0 }]);
    setSavedItemsSearches(['']);
    setReference('');
    setDueDate('');
    setContactName('');
    setContactNumber('');
    setFormErrors({});
    clearDocumentExtractionState();
  };

  const handleOpenDialog = () => {
    setPurchaseOrderDepartment(getDefaultPurchaseOrderDepartment());
    setCreateDialogOpen(true);
    // Lazy load tenant branding only when dialog opens
    if (!tenantBranding) {
      fetchTenantBranding();
    }
    if (!projectsLoaded) {
      fetchProjects();
    }
  };

  // Handle duplication mode toggle
  const handleDuplicationModeChange = (event) => {
    const enabled = event.target.checked;
    setIsDuplicationMode(enabled);
    
    if (!enabled) {
      // Reset to fresh PO mode
      setSelectedPOToDuplicate(null);
      setSelectedVendor(null);
      setSelectedProject(null);
      setPurchaseOrderDepartment(getDefaultPurchaseOrderDepartment());
      setDeliveryAddress({ line1: '', line2: '', city: '', postcode: '' });
      setItems([{ name: '', quantity: 1, units: 'Each', price: 0 }]);
      setSavedItemsSearches(['']);
      setReference('');
      setDueDate('');
      setContactName('');
      setContactNumber('');
      clearDocumentExtractionState();
    }
  };

  // Handle PO selection for duplication
  const handlePOSelection = async (po) => {
    if (!po) {
      setSelectedPOToDuplicate(null);
      setSelectedVendor(null);
      setSelectedProject(null);
      setPurchaseOrderDepartment(getDefaultPurchaseOrderDepartment());
      setDeliveryAddress({ line1: '', line2: '', city: '', postcode: '' });
      setItems([{ name: '', quantity: 1, units: 'Each', price: 0 }]);
      setSavedItemsSearches(['']);
      setReference('');
      setDueDate('');
      setContactName('');
      setContactNumber('');
      clearDocumentExtractionState();
      return;
    }

    setSelectedPOToDuplicate(po);
    
    // Pre-populate form with selected PO data
    // Set vendor (create vendor object from PO data)
    if (po.vendor_id) {
      setSelectedVendor({
        vendor_id: po.vendor_id,
        name: po.vendor_name
      });
    }
    
    // Set delivery address
    setDeliveryAddress({
      line1: po.delivery_address_line1 || '',
      line2: po.delivery_address_line2 || '',
      city: po.delivery_city || '',
      postcode: po.delivery_postcode || ''
    });
    
    // Set reference and other fields
    setReference(po.reference || '');
    setDueDate(po.due_date ? new Date(po.due_date).toISOString().split('T')[0] : '');
    setContactName(po.contact_name || '');
    setContactNumber(po.contact_number || '');
    setPurchaseOrderDepartment(po.department || getDefaultPurchaseOrderDepartment());
    
    // Set items from PO
    if (po.items && po.items.length > 0) {
      setItems(po.items.map(item => ({
        name: item.name || '',
        quantity: item.quantity || 1,
        units: item.units || 'Each',
        price: item.price || 0
      })));
      setSavedItemsSearches(new Array(po.items.length).fill(''));
    }
  };

  // Fetch all purchase orders
  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      const response = await purchaseOrdersApi.getAll(true);
      setPurchaseOrders(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching purchase orders:', err);
      setError('Failed to load purchase orders. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      setProjectsLoading(true);
      const response = await projectsApi.getAll(null, currentUser?.role === 'Office');
      setProjects(Array.isArray(response.data) ? response.data : []);
      setProjectsLoaded(true);
    } catch (err) {
      console.error('Error fetching projects for purchase order:', err);
      setFormErrors(prev => ({
        ...prev,
        project: 'Projects could not be loaded. You can still create a standalone purchase order.'
      }));
    } finally {
      setProjectsLoading(false);
    }
  };

  // Fetch tenant branding
  const fetchTenantBranding = async () => {
    try {
      const response = await tenantApi.getBranding();
      setTenantBranding(response.data.branding);
    } catch (error) {
      console.error('Error fetching tenant branding:', error);
    }
  };

  useEffect(() => {
    fetchPurchaseOrders();
    // Don't fetch tenant branding on page load - only when dialog opens
  }, []);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, px: { xs: 1, sm: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 2 }, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 3 
        }}>
          <Typography component="h1" variant="h5" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
            Purchase Orders
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          >
            Create Purchase Order
          </Button>
        </Box>

        {/* Search Bar and Department Filter */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={hasDepartments ? 8 : 12} md={hasDepartments ? 9 : 12}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by vendor, PO number, project, reference, or creator..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearSearch} size="small">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          {hasDepartments && (
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="department-filter-label">Department</InputLabel>
                <Select
                  labelId="department-filter-label"
                  value={departmentFilter}
                  onChange={handleDepartmentFilterChange}
                  label="Department"
                >
                  <MenuItem value="all">All Departments</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept} value={dept}>
                      {dept}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
        
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Mobile view - Cards */}
            {isMobile ? (
              <>
                <Box>
                  {paginatedPurchaseOrders.map((po) => (
                    <PurchaseOrderCard key={po.purchase_order_id} po={po} />
                  ))}
                  {filteredPurchaseOrders.length === 0 && (
                    <Paper sx={{ p: 3, textAlign: 'center' }}>
                      <Typography color="text.secondary">
                        {searchQuery ? 'No purchase orders match your search' : 'No purchase orders found'}
                      </Typography>
                    </Paper>
                  )}
                </Box>
                {filteredPurchaseOrders.length > 0 && (
                  <TablePagination
                    component="div"
                    count={filteredPurchaseOrders.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                  />
                )}
              </>
            ) : (
              /* Desktop view - Table */
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Purchase Order No</TableCell>
                      <TableCell>Origin</TableCell>
                      <TableCell>Created Date</TableCell>
                      <TableCell>Created By</TableCell>
                      <TableCell>Vendor</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Items</TableCell>
                      <TableCell align="right">NETT Total</TableCell>
                      <TableCell>Delivery Address</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPurchaseOrders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} align="center">
                          {searchQuery ? 'No purchase orders match your search' : 'No purchase orders found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedPurchaseOrders.map((po) => (
                        <TableRow 
                          key={po.purchase_order_id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleViewPurchaseOrder(po.purchase_order_id)}
                        >
                          <TableCell>PO-{po.purchase_order_no || po.purchase_order_id}</TableCell>
                          <TableCell>
                            {po.project_id ? (
                              <Link 
                                component={RouterLink} 
                                to={`/projects/${po.project_id}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Chip
                                  label={`Project #${po.project_no || po.project_id}`}
                                  size="small"
                                  variant="outlined"
                                />
                              </Link>
                            ) : (
                              <Chip label="Standalone" size="small" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell>
                            {po.created_date ? format(new Date(po.created_date), 'dd MMM yyyy') : 'N/A'}
                          </TableCell>
                          <TableCell>{po.created_by_name || '-'}</TableCell>
                          <TableCell>{po.vendor_name || '-'}</TableCell>
                          <TableCell>{po.reference || '-'}</TableCell>
                          <TableCell>
                            {po.due_date ? format(new Date(po.due_date), 'dd MMM yyyy') : '-'}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={po.status || 'New'} 
                              color={po.status === 'Completed' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {po.items ? po.items.length : 0} items
                          </TableCell>
                          <TableCell align="right">
                            {po.items ? formatCurrency(calculateNettTotal(po.items)) : formatCurrency(0)}
                          </TableCell>
                          <TableCell>
                            {po.delivery_address_line1 || po.project_address_line1 ? (
                              <>
                                {po.delivery_address_line1 || po.project_address_line1}
                                {(po.delivery_address_line2 || po.project_address_line2) ? `, ${po.delivery_address_line2 || po.project_address_line2}` : ''}
                                {(po.delivery_city || po.project_city) ? `, ${po.delivery_city || po.project_city}` : ''}
                                {(po.delivery_postcode || po.project_postcode) ? `, ${po.delivery_postcode || po.project_postcode}` : ''}
                              </>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <IconButton
                              size="small"
                              onClick={() => handleViewPurchaseOrder(po.purchase_order_id)}
                              aria-label={`View purchase order ${po.purchase_order_id}`}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenDeleteDialog(po)}
                              aria-label={`Delete purchase order ${po.purchase_order_id}`}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            
            {/* Pagination for desktop */}
            {!isMobile && filteredPurchaseOrders.length > 0 && (
              <TablePagination
                component="div"
                count={filteredPurchaseOrders.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            )}
          </>
        )}
      </Paper>

      {/* Mobile FAB */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="create purchase order"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={handleOpenDialog}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        fullScreen={isMobile}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this purchase order?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, md: 3 }, gap: 1 }}>
          <Button onClick={handleCloseDeleteDialog} fullWidth={isMobile} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeletePurchaseOrder}
            color="error"
            variant="contained"
            fullWidth={isMobile}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Purchase Order Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Create Purchase Order</DialogTitle>
        <DialogContent>
          {formErrors.general && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors.general}
            </Alert>
          )}
          
          <Grid container spacing={2}>
            {/* Duplication Mode Toggle */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isDuplicationMode}
                    onChange={handleDuplicationModeChange}
                    color="primary"
                  />
                }
                label="Duplicate from existing purchase order"
                sx={{ mb: 1 }}
              />
            </Grid>

            {/* PO Selector for Duplication */}
            {isDuplicationMode && (
              <Grid item xs={12}>
                <Autocomplete
                  options={purchaseOrders}
                  getOptionLabel={(option) => 
                    `PO-${option.purchase_order_no || option.purchase_order_id} - ${option.vendor_name} - ${option.reference || 'No reference'}${option.project_id ? ' (Project)' : ' (Standalone)'}`
                  }
                  value={selectedPOToDuplicate}
                  onChange={(event, newValue) => handlePOSelection(newValue)}
                  loading={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Purchase Order to Duplicate"
                      required
                      helperText="Choose any purchase order to duplicate its details and items, then optionally select a project for the new purchase order."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          PO-{option.purchase_order_no || option.purchase_order_id} - {option.vendor_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.reference || 'No reference'} • {option.items ? option.items.length : 0} items • {formatCurrency(calculateNettTotal(option.items || []))} • {option.project_id ? 'Project PO' : 'Standalone PO'}
                        </Typography>
                      </Box>
                      <Chip 
                        label={option.status || 'New'} 
                        size="small" 
                        color={option.status === 'Completed' ? 'success' : 'default'}
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  )}
                />
              </Grid>
            )}

            {selectedPOToDuplicate && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>Duplicating from:</strong> PO-{selectedPOToDuplicate.purchase_order_no || selectedPOToDuplicate.purchase_order_id} - {selectedPOToDuplicate.vendor_name}
                  </Typography>
                  <Typography variant="body2">
                    All fields are pre-filled but can be modified before creating.
                  </Typography>
                </Alert>
              </Grid>
            )}

            {/* Optional project link */}
            <Grid item xs={12}>
              <Autocomplete
                options={projects}
                value={selectedProject}
                onChange={(event, newValue) => handleProjectChange(newValue)}
                getOptionLabel={getProjectLabel}
                isOptionEqualToValue={(option, value) => option.project_id === value.project_id}
                loading={projectsLoading}
                disabled={creating}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Link to Project (Optional)"
                    error={!!formErrors.project}
                    helperText={formErrors.project || 'Select a project to make this purchase order available from the project as well.'}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {projectsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return (
                    <Box component="li" key={key} {...optionProps}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getProjectLabel(option)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[option.address_line1, option.city, option.postcode].filter(Boolean).join(', ') || option.status}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
              />
            </Grid>

            {/* Vendor */}
            <Grid item xs={12}>
              <VendorSelector
                selectedVendor={selectedVendor}
                onVendorChange={handleVendorChange}
                disabled={creating || (isDuplicationMode && !selectedPOToDuplicate)}
              />
              {formErrors.vendor && (
                <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                  {formErrors.vendor}
                </Typography>
              )}
              {isDuplicationMode && selectedPOToDuplicate && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Vendor is pre-filled from the selected purchase order, but you can change it if needed.
                </Typography>
              )}
            </Grid>

            {/* Reference */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reference *"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                disabled={creating}
                placeholder="Enter purchase order reference"
                error={!!formErrors.reference}
                helperText={formErrors.reference}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="purchase-order-department-label">Department</InputLabel>
                <Select
                  labelId="purchase-order-department-label"
                  value={purchaseOrderDepartment}
                  onChange={(e) => setPurchaseOrderDepartment(e.target.value)}
                  label="Department"
                  disabled={Boolean(selectedProject)}
                >
                  {departmentOptions.map((dept) => (
                    <MenuItem key={dept} value={dept}>
                      {dept}
                    </MenuItem>
                  ))}
                </Select>
                {selectedProject && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Linked purchase orders use the project department.
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Due Date */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                disabled={creating}
                InputLabelProps={{
                  shrink: true,
                }}
                placeholder=""
                inputProps={{
                  placeholder: ""
                }}
              />
            </Grid>

            {/* Contact Details */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Contact Details (Optional)
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Name"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                disabled={creating}
                placeholder="Enter contact person name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Number"
                value={contactNumber}
                onChange={(e) => setContactNumber(e.target.value)}
                disabled={creating}
                placeholder="Enter contact phone number"
              />
            </Grid>

            {/* Delivery Address */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Delivery Address (Optional)
              </Typography>
              <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {selectedProject && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => prefillAddress('project')}
                  >
                    Use Project Address
                  </Button>
                )}
                <Button 
                  size="small" 
                  variant="outlined" 
                  onClick={() => prefillAddress('company')}
                  disabled={!tenantBranding?.company_address}
                  title={!tenantBranding?.company_address ? 'Company address not set in tenant settings' : ''}
                >
                  Use Company Address
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={deliveryAddress.line1}
                onChange={(e) => handleDeliveryAddressChange('line1', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={deliveryAddress.line2}
                onChange={(e) => handleDeliveryAddressChange('line2', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="City"
                value={deliveryAddress.city}
                onChange={(e) => handleDeliveryAddressChange('city', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Postcode"
                value={deliveryAddress.postcode}
                onChange={(e) => handleDeliveryAddressChange('postcode', e.target.value)}
              />
            </Grid>

            {/* Items */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
                <Typography variant="h6">Items *</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <input
                    ref={extractionFileInputRef}
                    type="file"
                    hidden
                    multiple
                    accept="image/*,.pdf,.eml,.txt,.html,.htm"
                    onChange={handleDocumentFileSelect}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<AttachFileIcon />}
                    onClick={() => extractionFileInputRef.current?.click()}
                    disabled={creating || extractingItems}
                  >
                    Add From Document
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={extractingItems ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                    onClick={handleExtractItemsFromDocument}
                    disabled={creating || extractingItems || documentFiles.length === 0}
                  >
                    {extractingItems ? 'Extracting...' : 'Extract Items'}
                  </Button>
                  <Button startIcon={<AddIcon />} onClick={addItem}>
                    Add Item
                  </Button>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Upload a supplier PDF, image, or email file and Struxor will extract line items into this purchase order.
              </Typography>
              {documentFiles.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {documentFiles.map((file, index) => (
                    <Chip
                      key={`${file.name}-${index}`}
                      label={file.name}
                      onDelete={() => handleRemoveDocumentFile(index)}
                      size="small"
                    />
                  ))}
                </Box>
              )}
              {documentExtractionError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {documentExtractionError}
                </Alert>
              )}
              {documentExtractionWarnings.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {documentExtractionWarnings.join(' ')}
                </Alert>
              )}
              {documentExtractionNotes && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {documentExtractionNotes}
                </Alert>
              )}
            </Grid>

            {items.map((item, index) => (
              <Grid item xs={12} key={index}>
                <Paper sx={{ p: 2, mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1">Item {index + 1}</Typography>
                    {items.length > 1 && (
                      <IconButton 
                        onClick={() => removeItem(index)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      {/* Saved Items Search - Hide for Site Manager */}
                      {currentUser?.role !== 'Site Manager' && (
                        <SavedItemsSearch
                          value={savedItemsSearches[index] || ''}
                          onChange={(value) => handleSavedItemsSearchChange(index, value)}
                          onSelect={(savedItem) => handleSavedItemSelect(index, savedItem)}
                          placeholder="Search saved items or type new item name..."
                          label="Search saved items"
                          size="small"
                          sx={{ mb: 1 }}
                        />
                      )}
                      
                      {/* Item Name Field - Multiline */}
                      <TextField
                        fullWidth
                        label="Item Name *"
                        value={item.name}
                        onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                        error={!!formErrors[`item_${index}_name`]}
                        helperText={formErrors[`item_${index}_name`]}
                        multiline
                        minRows={2}
                        maxRows={4}
                      />
                    </Grid>
                    <Grid item xs={4} sm={2}>
                      <TextField
                        fullWidth
                        label="Quantity *"
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        error={!!formErrors[`item_${index}_quantity`]}
                        helperText={formErrors[`item_${index}_quantity`]}
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                    <Grid item xs={4} sm={2}>
                      <TextField
                        fullWidth
                        label="Units"
                        value={item.units}
                        onChange={(e) => handleItemChange(index, 'units', e.target.value)}
                        placeholder="Each"
                      />
                    </Grid>
                    <Grid item xs={4} sm={4}>
                      <TextField
                        fullWidth
                        label="Price (£) *"
                        type="number"
                        value={item.price}
                        onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                        error={!!formErrors[`item_${index}_price`]}
                        helperText={formErrors[`item_${index}_price`]}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}

            {/* Total */}
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'right', mt: 2 }}>
                <Typography variant="h6">
                  NETT Total: {formatCurrency(calculateNettTotal(items))}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={creating}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreatePO} 
            variant="contained"
            disabled={creating}
          >
            {creating ? <CircularProgress size={20} /> : (isDuplicationMode ? 'Duplicate Purchase Order' : 'Create Purchase Order')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default PurchaseOrders;
